import { useState } from "react";
import { usePageMeta } from "@/lib/seo";
import { Mail, MapPin, Phone } from "lucide-react";
import { apiClient } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Contact() {
  usePageMeta("Contact Us", "Get in touch with True Impact Peptides for research inquiries, order questions, or wholesale requests.", "/contact");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await apiClient.post("/contact", form);
      toast.success("Message sent. Our team will respond within 1-2 business days.");
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Too many requests. Please try again later.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 grid lg:grid-cols-2 gap-14" data-testid="contact-page">
      <div>
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-brandblue-600">Contact</span>
        <h1 className="mt-3 text-4xl tracking-tighter font-medium text-slate-900 font-display">Get in Touch</h1>
        <p className="mt-5 text-slate-600 leading-relaxed">Have a question about our research compounds, batch documentation, or wholesale inquiries? Our team is here to help.</p>

        <div className="mt-8 space-y-4 text-sm text-slate-600">
          <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-brandblue-600" /> info@trueimpactpeptides.com</div>
          <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-brandblue-600" /> (657) 768-2609</div>
          <div className="flex items-center gap-3"><MapPin className="h-4 w-4 text-brandblue-600" /> Research Operations Center, United States</div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
        {/* Honeypot field: hidden from real users, bots often auto-fill it */}
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
          <Label htmlFor="website">Website</Label>
          <Input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        </div>
        <div><Label>Name</Label><Input required maxLength={200} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" /></div>
        <div><Label>Email</Label><Input type="email" required maxLength={200} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" /></div>
        <div><Label>Subject</Label><Input required maxLength={200} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input" /></div>
        <div><Label>Message</Label><Textarea required rows={5} maxLength={3000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" /></div>
        <Button type="submit" disabled={loading} className="rounded-full btn-lift" data-testid="contact-submit-btn">{loading ? "Sending..." : "Send Message"}</Button>
      </form>
    </div>
  );
}
